import DjangoMessages from 'components/DjangoMessages';
import {RadioOption} from 'components/Form/Base/BaseRadio';
import Checkbox from 'components/Form/Checkbox';
import Field from 'components/Form/Field';
import FieldDivider from 'components/Form/FieldDivider';
import FormActions from 'components/Form/FormActions';
import InputText from 'components/Form/InputText';
import Radio from 'components/Form/Radio';
import SubmitButton from 'components/Form/SubmitButton';
import TextArea from 'components/Form/TextArea';
import Information from 'components/Information';
import SideBySide from 'components/Layout/SideBySide';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import TooltipIcon from 'components/Tooltip/TooltipIcon';
import {useEffect, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {c, jt, t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiErrorAxios} from 'utils/request-axios/utils';

import {BackendData, DxFormData, DXSettings} from '../../types';
import {sendingSettingsLinks} from '../../utils';
import {useSubmitDxForm} from '../services';
import DXInstructions from './DXInstructions';

const EnableAddressCollection = () => {
    return (
        <Field
            label={t`Enable Email Address Collection`}
            scrollIntoViewOnError={true}
            type="value"
        >
            <Checkbox name="defaultEnabled" />
        </Field>
    );
};

const SendingMethods = ({isSfConnected}: {isSfConnected: boolean}) => {
    const options: RadioOption[] = [
        {
            label: t`Client's "Client Inbox Users” (Send via Client Inbox)`,
            value: 'clientinbox_user',
        },
        {
            disabled: isSfConnected,
            disabledReason: isSfConnected
                ? t`Not supported. Client contact information must be managed in Salesforce.`
                : undefined,
            label: t`Client's Default Contact (Secure Send)`,
            value: 'default_contact',
        },
    ];

    return (
        <Radio
            className="mt-2 border-l border-grey-300 pl-4"
            label={t`Where Email Address is saved`}
            name="target"
            options={options}
            required={true}
            requiredType="hidden"
            scrollIntoViewOnError={true}
        />
    );
};

const dxNoticeBodyHelpCenterLink = (
    <Link
        href={sendingSettingsLinks.dxNoticeHelpCenterLink} // TODO: Needs to be updated
        isOpenInNewTab={true}
        key="help-center"
    >{t`Help Center`}</Link>
);

const DxNotice = () => {
    return (
        <>
            <FieldDivider>
                {t`Email Address Registration Request`}
                <TooltipIcon
                    aria-label="simple-field-tooltip"
                    href={sendingSettingsLinks.dxNoticeHelpCenterLink}
                    message={t`A request document that will be included in the envelope, containing your customized message, registration website information, and the Client's Registration Code.`}
                    size="base"
                />
            </FieldDivider>
            <InputText
                label={
                    <div className="flex items-center gap-2">
                        {c('dx-helper').t`Title`}
                        <TooltipIcon
                            href="https://help.makeleaps.jp/ja/articles/4108694#h_b385b3699f"
                            message={t`This title also appears on the envelope's Cover Letter as the name of the document.`}
                            size="base"
                        />
                    </div>
                }
                maxLength={80}
                name="noticeTitle"
                required={true}
                requiredType="hidden"
                scrollIntoViewOnError={true}
            />
            <TextArea
                label={
                    <div className="flex items-center gap-2">
                        {c('dx-helper').t`Message`}
                        <TooltipIcon
                            href="http://example.org"
                            message={t`If your title and message causes the Registration Request to be longer than one page, you will see an error upon clicking Save. For details, see the ${dxNoticeBodyHelpCenterLink} (Japanese site).`}
                            size="base"
                        />
                    </div>
                }
                maxLength={800}
                name="noticeBody"
                required={true}
                requiredType="hidden"
                resize="auto"
                rows={4}
                scrollIntoViewOnError={true}
            />
        </>
    );
};

const ConfirmationEmail = () => {
    return (
        <>
            <FieldDivider>{c('dx-helper')
                .t`"Registration Complete" Email`}</FieldDivider>
            <TextArea
                helpText={t`When set, this custom message will be inserted into the automated "Registration Complete" email, alongside our standard content.`}
                label={c('dx-helper').t`Message`}
                markOptional={true}
                maxLength={400}
                name="confirmationEmailBody"
                resize="auto"
                rows={4}
                scrollIntoViewOnError={true}
            />
        </>
    );
};

const useInitialValue = (dxSettings: DXSettings | null): DxFormData => {
    const [result] = useState<DxFormData>(() => {
        return {
            attachInstructions: dxSettings
                ? dxSettings.attachInstructions
                : false,
            confirmationEmailBody: dxSettings
                ? dxSettings.confirmationEmailBody
                : '',
            defaultEnabled: dxSettings ? dxSettings.defaultEnabled : false,
            instructions: dxSettings ? dxSettings.instructions : null,
            noticeBody: dxSettings ? dxSettings.noticeBody : '',
            noticeTitle: dxSettings ? dxSettings.noticeTitle : '',
            target: dxSettings ? dxSettings.target : null,
        };
    });
    return result;
};

const Page = () => {
    const backendData = useBackend<BackendData>();
    const {dxSettings, isSfConnected} = backendData.data;
    const initialData = useInitialValue(dxSettings);
    const methods = useForm<DxFormData>({
        defaultValues: initialData,
    });

    const [instructionsFile, setInstructionsFile] = useState<File | null>(null);
    const [navigating, setNavigating] = useState<boolean>(false);

    const dxApiSubmit = useSubmitDxForm({
        initialData: initialData,
        instructionsFile: instructionsFile,
    });

    const dxHelperHelpCenterLink = (
        <Link
            href={sendingSettingsLinks.dxHelperHelpCenterLink}
            isOpenInNewTab={true}
            key="help-center"
        >{t`Help Center`}</Link>
    );

    useEffect(() => {
        const handleBeforeUnload = () => {
            setNavigating(true);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    });

    const disabled = methods.formState.isSubmitting || navigating;
    const isEnabled = methods.watch('defaultEnabled');

    const handleSubmit = async (formData: DxFormData) => {
        formData.confirmationEmailBody = formData.confirmationEmailBody
            .trimStart()
            .trimEnd();

        await dxApiSubmit
            .handleSubmit(formData)
            .then(() => {
                setUrl(sendingSettingsLinks.sendingSettingsView);
            })
            .catch(onApiErrorAxios(methods));
    };

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
                <DjangoMessages className="mb-4" />
                <NavigationTabs.Partner selection="sending_settings" />
                <div>
                    <SectionHeader
                        button={{
                            disabled: disabled,
                            kind: 'primary',
                            label: t`Save`,
                            onClick: () => methods.handleSubmit(handleSubmit)(),
                            type: 'button',
                        }}
                        subtitle={jt`Collect email addresses from Clients so you can transition to electronic sending methods. For details, see the ${dxHelperHelpCenterLink} (Japanese site).`}
                        title={t`Email Address Collection`}
                    />
                    <SideBySide
                        className="mt-4"
                        classNameLabel="w-[14rem] max-w-[14rem]"
                    >
                        <EnableAddressCollection />
                        <SendingMethods isSfConnected={isSfConnected} />
                        <DxNotice />
                        <DXInstructions
                            {...{
                                instructionsFile,
                                instructionsProgress:
                                    dxApiSubmit.instructionsProgress,
                                readonly: disabled,
                                setInstructionsFile,
                                setInstructionsProgress:
                                    dxApiSubmit.setInstructionsProgress,
                            }}
                        />
                        <ConfirmationEmail />
                        {isEnabled ? null : (
                            <Information icon="warning" size="sm" type="alert">
                                {t`Email Collection is currently off.`}
                            </Information>
                        )}
                        <FormActions align="left">
                            <SubmitButton
                                disabled={disabled}
                            >{t`Save`}</SubmitButton>
                            <LinkButton
                                disabled={disabled}
                                href={sendingSettingsLinks.sendingSettingsView}
                            >{t`Cancel`}</LinkButton>
                        </FormActions>
                    </SideBySide>
                </div>
            </form>
        </FormProvider>
    );
};
export default Page;
